import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useEffect, useState } from 'react'
import {
	DeleteUserAccountDataProps,
	GetCurrentUserObjectData,
	GetMfaResponseData,
	GetUserPermissionSetsDataResponse,
	SearchUsersDataResponse,
	SearchUsersParamsProps,
	UpdateCurrentUserObjectDataProps,
	UploadAvatarDataProps,
} from '../../lib/api/types/user.interfaces'
import { UserService } from '../../lib/api'
import { handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

export const useUser = (
	options?: Partial<QueryObserverOptions<GetCurrentUserObjectData>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['user'],
		queryFn: () => UserService.getUser(),
		enabled: isAuthenticated,
		...options,
	})
}

export const useUpdateUserProfile = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['userProfileUpdate'],
		mutationFn: ({
			data,
		}: {
			data: UpdateCurrentUserObjectDataProps
		}) => UserService.updateUserProfile({ data}),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				type: 'success',
				message: 'Your profile successfully updated.',
				autoClose: 2000,
			})
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useAllUsers = (params: SearchUsersParamsProps,
	options?: Partial<QueryObserverOptions<SearchUsersDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const [debouncedQuery, setDebouncedQuery] = useState(params?.query)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedQuery(params?.query)
		}, 500)

		return () => clearTimeout(handler)
	}, [params?.query])

	return useQuery({
		queryKey: ['users', debouncedQuery?.trim()],
		queryFn: () =>
			UserService.search({
				params: { ...params, query: debouncedQuery?.trim() },
			}),
		enabled: isAuthenticated && Boolean(debouncedQuery?.trim()?.length > 3),
		...options,
	})
}

export const useUserPermissionSets = (
	options?: Partial<QueryObserverOptions<GetUserPermissionSetsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetUserPermissionSetsDataResponse>({
		queryKey: ['users'],
		queryFn: () => UserService.getUserPermissionSets(),
		enabled: isAuthenticated,
		...options,
	})
}

export const useUploadAvatar = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['userAvatarUpload'],
		mutationFn: ({
			data,
		}: {
			data: UploadAvatarDataProps
		}) => UserService.uploadAvatar({ data}),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteAvatar = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['userAvatarDelete'],
		mutationFn: () => {
			return UserService.deleteAvatar().then(() => true)
		},
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				type: 'success',
				message: 'Avatar successfully deleted.',
				autoClose: 2000,
			})
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
			return error
		}
	}

	return { ...mutation, mutateAsync }
}

export const useChangePassword = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['changePasswordRequest'],
		mutationFn: async () => {
			return await UserService.changePasswordRequest()
		},
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				type: 'success',
				message: 'Confirmation to change password has been sent to your mail.',
			})
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGetMfa = ({ options }: {
	options?: Partial<QueryObserverOptions<GetMfaResponseData>>
}) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['userMfa'],
		queryFn: () => UserService.getMfa(),
		enabled: isAuthenticated,
		retry: false,
		...options,
	})
}

export const useToggleMfa = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['toggleMfaRequest'],
		mutationFn: async ({ username,
			password,
			value,
			mfa_token,
			otp,
		}: {
			username?: string
			password?: string
			value: boolean
			mfa_token?: string
			otp?: string
		}) => {
			return await UserService.toggleMfa({ username,
				password,
				value,
				mfa_token,
				otp,
			})
		},
		onError: (error) => error,
		onSuccess: (data) => {
			if (data?.data?.success && data?.data?.data?.redirectUrl) {
				window.location.href = data.data.data.redirectUrl
			}
			toastCaller({
				type: 'success',
				message: 'MFA status changed.',
			})
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
			queryClient.invalidateQueries({
				queryKey: ['userMfa'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
			return error
		}
	}

	return { ...mutation, mutateAsync }
}

// useConfirmPassword
export const useConfirmPassword = () => {
	const mutation = useMutation({
		mutationKey: ['confirmPassword'],
		mutationFn: ({ password, email }: { password: string; email: string }) =>
			UserService.confirmPassword(password, email),
		onError: () => toastCaller({ message: 'Wrong password!' }),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
			return error
		}
	}

	return { ...mutation, mutateAsync }
}

//verify otp
export const useVerifyOtp = () => {
	const mutation = useMutation({
		mutationKey: ['verifyOtp'],
		mutationFn: ({ mfaToken, otp }: { mfaToken: string; otp: string }) =>
			UserService.verifyOtp(mfaToken, otp),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
			return error
		}
	}

	return { ...mutation, mutateAsync }
}

// delete account (only personal)

export const useDeleteAccount = () => {
	const mutation = useMutation({
		mutationKey: ['deleteAccount'],
		mutationFn: (data: DeleteUserAccountDataProps) =>
			UserService.deleteAccount(data),
		onError: (error) => handleApiError(error),
		onSuccess: () =>
			toastCaller({
				type: 'success',
				message: 'Your account has been successfully deleted!',
			}),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
